import React, { useState, useEffect } from "react";
import {
  Box,
  ThemeProvider,
  createTheme,
  CssBaseline,
  useMediaQuery,
} from "@mui/material";
import Sidebar from "./components/Sidebar";
import AdPanel from "./components/AdPanel";
import Home from "./pages/Home";
import AIHumanizer from "./pages/AIHumanizer";
import GrammarChecker from "./pages/GrammarChecker";
import PlagiarismChecker from "./pages/PlagiarismChecker";
import AIChat from "./pages/AIChat";
import ImageGenerator from "./pages/ImageGenerator";
import Summarizer from "./pages/Summarizer";
import Translator from "./pages/Translator";
import CitationGenerator from "./pages/CitationGenerator";
import AIDetector from "./pages/AIDetector";
import TextRewriter from "./pages/TextRewriter";
import SmartFlow from "./pages/SmartFlow";
import Premium from "./pages/Premium";
import SignIn from "./pages/SignIn";
import Profile from "./pages/Profile";
import Settings from "./pages/Settings";
import ManagePlan from "./pages/ManagePlan";
import Navbar from "./components/Navbar";
import { useServerWarmup } from "./hooks/useServerWarmup";
import "./App.css";
import { onAuthChange, logOut } from "./firebase";

function App() {
  useServerWarmup(); // ping backend on first load to wake Render free tier

  const [currentPage, setCurrentPage] = useState(() => {
    return localStorage.getItem("cowrite.currentPage") || "home";
  });

  useEffect(() => {
    localStorage.setItem("cowrite.currentPage", currentPage);
  }, [currentPage]);

  const [darkMode, setDarkMode] = useState(() => {
    const saved = localStorage.getItem("cowrite.darkMode");
    return saved !== null ? saved === "true" : true; // default: dark
  });
  const [userName, setUserName] = useState<string | null>(null);
  const [userPlan, setUserPlan] = useState<"Free" | "Pro" | "Team">("Free");
  const [isSidebarCollapsed, setIsSidebarCollapsed] = useState(false);
  const [mobileSidebarOpen, setMobileSidebarOpen] = useState(false);

  const themeForMedia = createTheme();
  const isMobile = useMediaQuery(themeForMedia.breakpoints.down("md"));

  useEffect(() => {
    // Expose navigation to global window for static content
    (window as any).navigateToPage = (page: string) => {
      setCurrentPage(page);
    };
    (window as any).openMobileSidebar = () => {
      setMobileSidebarOpen(true);
    };

    // Firebase Auth Listener
    const unsubscribe = onAuthChange((user) => {
      if (user) {
        const name = user.displayName || user.email?.split("@")[0] || "User";
        setUserName(name);
      } else {
        setUserName(null);
      }
    });

    // Check localStorage for legacy/static page consistency (optional, but good for initial render)
    const stored = localStorage.getItem("cowrite.userName");
    if (stored) setUserName(stored);

    return () => unsubscribe();
  }, []);

  const handleSignOut = async () => {
    try {
      await logOut();
      setUserName(null);
      setCurrentPage("home");
    } catch (error) {
      console.error("Sign out failed:", error);
      // Even if firebase signout fails (network etc), we should probably clear local state
      setUserName(null);
      setCurrentPage("home");
    }
  };

  useEffect(() => {
    if (userName) localStorage.setItem("cowrite.userName", userName);
    else localStorage.removeItem("cowrite.userName");
  }, [userName]);

  // Reflect auth state on the static landing navbar button
  useEffect(() => {
    const btn = document.querySelector(
      "[data-static-login]",
    ) as HTMLButtonElement | null;
    if (!btn) return;

    if (userName) {
      btn.textContent = userName;
      btn.onclick = () => (window as any).navigateToPage?.("profile");
    } else {
      btn.textContent = "Login";
      btn.onclick = () => (window as any).navigateToPage?.("signin");
    }
  }, [userName]);

  useEffect(() => {
    localStorage.setItem("cowrite.darkMode", String(darkMode));
    if (darkMode) {
      document.body.classList.remove("light-theme");
      document.documentElement.style.backgroundColor = "#030303";
    } else {
      document.body.classList.add("light-theme");
      document.documentElement.style.backgroundColor = "#fcfcfc";
    }
  }, [darkMode]);

  // Expose toggleDarkMode to window for static HTML
  useEffect(() => {
    (window as any).toggleDarkMode = () => {
      setDarkMode((prev) => !prev);
    };
    return () => {
      delete (window as any).toggleDarkMode;
    };
  }, []); // Control particle background visibility based on currentPage
  useEffect(() => {
    const particleCanvas = document.getElementById("particle-canvas-container");
    const staticContent = document.getElementById("static-content");

    if (currentPage === "home") {
      if (particleCanvas) particleCanvas.style.setProperty("display", "block", "important");
      if (staticContent) staticContent.style.setProperty("display", "block", "important");
    } else {
      if (particleCanvas) particleCanvas.style.setProperty("display", "none", "important");
      if (staticContent) staticContent.style.setProperty("display", "none", "important");
    }
  }, [currentPage]);

  // Sync static content sidebar collapse margin via dynamic style tag
  useEffect(() => {
    const styleId = "sidebar-sync-styles";
    let styleTag = document.getElementById(styleId) as HTMLStyleElement;

    if (!styleTag) {
      styleTag = document.createElement("style");
      styleTag.id = styleId;
      document.head.appendChild(styleTag);
    }

    const margin = isSidebarCollapsed ? "88px" : "280px";
    styleTag.innerHTML = `
      @media (min-width: 1024px) {
        #static-content { padding-left: ${margin} !important; transition: padding-left 0.3s ease !important; }
        #static-content nav { left: ${margin} !important; transition: left 0.3s ease !important; }
      }
    `;

    return () => {
      // Keep style tag if still on home, or remove if leaving
    };
  }, [isSidebarCollapsed, currentPage]);

  const theme = createTheme({
    palette: {
      mode: darkMode ? "dark" : "light",
      primary: { main: "#3b82f6" }, // Improved primary for light mode
      secondary: { main: "#7c3aed" },
      background: {
        default: darkMode ? "#030303" : "#fcfcfc",
        paper: darkMode ? "#0a0a0a" : "#ffffff",
      },
      text: {
        primary: darkMode ? "#ffffff" : "#1a1a1a",
        secondary: darkMode ? "#4b5563" : "#64748b",
      },
    },
    typography: {
      fontFamily:
        '"Inter", "Poppins", "Roboto", "Helvetica", "Arial", sans-serif',
      h2: { fontWeight: 800, letterSpacing: "-0.6px" },
      h3: { fontWeight: 800, letterSpacing: "-0.5px" },
      h4: { fontWeight: 700 },
      h5: { fontWeight: 700 },
      h6: { fontWeight: 600 },
      button: { textTransform: "none", fontWeight: 700 },
    },
    shape: { borderRadius: 14 },
    components: {
      MuiPaper: {
        styleOverrides: {
          root: {
            backgroundImage: "none",
            border: "1px solid rgba(0,0,0,0.06)",
          },
        },
      },
      MuiCard: {
        styleOverrides: {
          root: {
            border: "1px solid rgba(0,0,0,0.06)",
            transition: "all .25s ease",
          },
        },
      },
      MuiButton: {
        styleOverrides: {
          root: {
            borderRadius: 12,
            padding: "10px 20px",
          },
        },
      },
    },
  });

  const renderPage = () => {
    switch (currentPage) {
      case "home":
        return (
          <Home onNavigate={setCurrentPage} userName={userName || undefined} />
        );
      case "paraphraser":
        return <TextRewriter />;
      case "humanizer":
        return <AIHumanizer darkMode={darkMode} setDarkMode={setDarkMode} />;
      case "flow":
        return <SmartFlow />;
      case "grammar":
        return <GrammarChecker />;
      case "detector":
        return <AIDetector />;
      case "plagiarism":
        return <PlagiarismChecker />;
      case "chat":
        return <AIChat />;
      case "image-generator":
        return <ImageGenerator />;
      case "summarizer":
        return <Summarizer />;
      case "translate":
        return <Translator />;
      case "citation":
        return <CitationGenerator />;
      case "settings":
        if (!userName) {
          setCurrentPage("signin");
          return null;
        }
        return (
          <Settings
            userName={userName}
            onNavigate={setCurrentPage}
            onSignOut={handleSignOut}
          />
        );
      case "premium":
        return <Premium />;
      case "profile":
        if (!userName) {
          setCurrentPage("signin");
          return null;
        }
        return <Profile userName={userName} onSignOut={handleSignOut} />;
      case "manage-plan":
        if (!userName) {
          setCurrentPage("signin");
          return null;
        }
        return (
          <ManagePlan
            userName={userName}
            userPlan={userPlan}
            onPlanChange={setUserPlan}
            onNavigate={setCurrentPage}
          />
        );
      case "signin":
        return (
          <SignIn
            darkMode={darkMode}
            onSignIn={(name) => {
              setUserName(name);
              setCurrentPage("home");
            }}
          />
        );
      default:
        return (
          <Home onNavigate={setCurrentPage} userName={userName || undefined} />
        );
    }
  };

  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      <Box
        sx={{
          display: "flex",
          minHeight: currentPage === "home" ? 0 : "100vh",
          position: "relative",
          bgcolor:
            currentPage === "home" ? "transparent" : "background.default",
        }}
      >
        <Sidebar
          currentPage={currentPage}
          onPageChange={setCurrentPage}
          darkMode={darkMode}
          collapsed={isSidebarCollapsed}
          setCollapsed={setIsSidebarCollapsed}
          userName={userName}
          isMobile={isMobile}
          mobileOpen={mobileSidebarOpen}
          onMobileClose={() => setMobileSidebarOpen(false)}
        />
        <Box
          sx={{
            flexGrow: 1,
            display: "flex",
            flexDirection: "column",
            minHeight: currentPage === "home" ? 0 : "100vh",
            position: "relative",
            minWidth: 0,
            ml: isMobile ? 0 : isSidebarCollapsed ? "88px" : "280px",
            transition: "margin-left 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
            pointerEvents: currentPage === "home" ? "none" : "auto",
          }}
        >
          {currentPage !== "home" && currentPage !== "signin" && (
            <Navbar
              currentPage={currentPage}
              onPageChange={setCurrentPage}
              darkMode={darkMode}
              onToggleDarkMode={() => setDarkMode(!darkMode)}
              userName={userName}
              userPlan={userPlan}
              onSignIn={(name) => {
                setUserName(name);
                setCurrentPage("home");
              }}
              onSignOut={handleSignOut}
              onMenuOpen={() => setMobileSidebarOpen(true)}
            />
          )}
          <Box
            component="main"
            sx={{
              flex: currentPage === "home" ? "initial" : 1,
              p: currentPage === "home" ? 0 : 4,
              pt: currentPage === "home" ? 0 : 0, // Navbar height is handled by Navbar's sticky
              bgcolor:
                currentPage === "home" ? "transparent" : "background.default",
              minWidth: 0,
            }}
          >
            {renderPage()}
          </Box>
        </Box>
      </Box>
    </ThemeProvider>
  );
}

export default App;
